import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import { authAPI, webAuthnAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { registerPush } from '../utils/push';
import { Fingerprint } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { setUser, setSessionToken } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [hasBiometrics, setHasBiometrics] = useState(false);

  // Check for registered biometrics when email loses focus
  const handleEmailBlur = useCallback(async () => {
    if (!email || !email.includes('@')) return;
    try {
      const res = await webAuthnAPI.check(email);
      setHasBiometrics(res.data.has_credentials);
    } catch {
      setHasBiometrics(false);
    }
  }, [email]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    try {
      const response = await authAPI.login(email, password);
      const userData = response.data.user;
      setUser(userData);
      if (response.data.session_token) setSessionToken(response.data.session_token, rememberMe);
      if (userData.account_status === 'registered') {
        navigate('/set-password');
      } else {
        registerPush();
        navigate('/dashboard');
      }
    } catch {
      setLoginError('Incorrect email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (!email) {
      setLoginError('Please enter your email address first.');
      return;
    }
    setBiometricLoading(true);
    setLoginError('');
    try {
      // 1. Get challenge from server
      const beginRes = await webAuthnAPI.loginBegin(email);
      const { challenge_id, options } = beginRes.data;

      // 2. Prompt device biometrics
      const credential = await startAuthentication({ optionsJSON: options });

      // 3. Verify with server
      const completeRes = await webAuthnAPI.loginComplete(challenge_id, credential);
      const userData = completeRes.data.user;
      setUser(userData);
      if (completeRes.data.session_token) setSessionToken(completeRes.data.session_token, true);
      registerPush();
      navigate('/dashboard');
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setLoginError('Biometric login was cancelled.');
      } else if (err.response?.data?.detail) {
        setLoginError(err.response.data.detail);
      } else {
        setLoginError('Biometric login failed. Please try your password.');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_f1fe9a42-12b3-4cb4-b6d2-85b551503a7f/artifacts/hqgml9p8_ncc%20logo.jpg"
            alt="Church Logo"
            className="w-16 h-16 rounded-2xl object-cover mx-auto mb-5 shadow-sm"
          />
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Church Service Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">Novaliches Christian Community</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              data-testid="email-input"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setLoginError(''); setHasBiometrics(false); }}
              onBlur={handleEmailBlur}
              className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-green-600/20 focus:border-green-600 outline-none transition-all"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              data-testid="password-input"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
              className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-green-600/20 focus:border-green-600 outline-none transition-all"
              placeholder="Enter your password"
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="remember-me-checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-green-600 accent-green-600 cursor-pointer"
              />
              <span className="text-sm text-slate-600">Remember me</span>
            </label>
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-sm text-green-700 font-medium hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="login-btn"
              className="flex-1 h-11 rounded-full bg-green-700 text-white font-semibold text-sm hover:bg-green-800 active:scale-[0.98] transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>

            {hasBiometrics && (
              <button
                type="button"
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                data-testid="biometric-login-btn"
                title="Sign in with biometrics"
                className="mt-2 w-11 h-11 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-200 active:scale-[0.95] transition-all disabled:opacity-50"
              >
                {biometricLoading
                  ? <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  : <Fingerprint size={18} />
                }
              </button>
            )}
          </div>

          {loginError && (
            <p data-testid="login-error-msg" className="text-sm text-rose-600 text-center -mt-1">
              {loginError}
            </p>
          )}
        </form>

      </div>
    </div>
  );
};

export default Login;
